import React, { useState, Fragment, useMemo } from 'react'

import PropTypes from 'prop-types'
import { v4 as uuid } from 'uuid'

import e2eClass from 'helpers/automation'

import { TabPanel, Tab, TabBar } from './styled'

const Tabs = ({
  tabs,
  content,
  initialTabIndex = 0,
  keepTabsMounted,
  barWidth,
  TabComponent,
  scrollableTabs,
  barPadding
}) => {
  const [tabEnabled, setEnabledTab] = useState(initialTabIndex)
  const id = useMemo(() => uuid(), [])

  const handleClick = (index, handleTabClick) => {
    setEnabledTab(index)
    handleTabClick && handleTabClick(index)
  }

  const getTabPanel = (component, index) => {
    const mountCondition = keepTabsMounted || tabEnabled === index
    const isActive = tabEnabled === index
    return (
      mountCondition && (
        <TabPanel
          id={`tabContent-${id}-${index}`}
          key={`tabContent-${index}`}
          aria-labelledby={`tab-${id}-${index}`}
          active={isActive}
          aria-hidden={!isActive}
          role="tabpanel"
        >
          {component}
        </TabPanel>
      )
    )
  }

  const TabToRender = TabComponent || <Tab />

  return (
    <Fragment>
      <TabBar
        width={barWidth}
        e2eClass={e2eClass('payments.tabBar')}
        scrollable={scrollableTabs}
        padding={barPadding}
        role="tablist"
      >
        {tabs.map(({ handleTabClick, title, disabled }, index) => (
          <li
            key={`list-item-${index}`}
            role="presentation"
            style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            {React.cloneElement(
              TabToRender,
              {
                key: `tab${index}`,
                id: `tab-${id}-${index}`,
                selected: tabEnabled === index,
                onClick: () => handleClick(index, handleTabClick),
                'aria-controls': tabEnabled === index ? `tabContent-${id}-${index}` : undefined,
                'aria-selected': tabEnabled === index,
                role: 'tab',
                style: { pointerEvents: disabled ? 'none' : 'auto' }
              },
              title
            )}
          </li>
        ))}
      </TabBar>
      {content.map((component, index) => getTabPanel(component, index))}
    </Fragment>
  )
}

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      handleTabClick: PropTypes.func
    })
  ).isRequired,
  content: PropTypes.arrayOf(PropTypes.node).isRequired,
  keepTabsMounted: PropTypes.bool,
  initialTabIndex: PropTypes.number,
  barWidth: PropTypes.string,
  TabComponent: PropTypes.element,
  scrollableTabs: PropTypes.bool
}

export default Tabs
